import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LangContext';
import { Link } from 'react-router-dom';
import { BookOpen, Users, Bot, Trophy, ChevronRight, TrendingUp, ClipboardList, MessageSquare, Activity, Calendar } from 'lucide-react';
import { displayPlayName } from '@/lib/playName';
import { useTeams } from '@/context/TeamContext';

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-card-steel border border-gold/30 rounded-lg p-5 hover-lift">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-md border ${accent ? 'border-gold bg-gold/10 text-gold' : 'border-gold/40 text-gold'} flex items-center justify-center`}>
          <Icon className="w-5 h-5" strokeWidth={1.6} />
        </div>
        <TrendingUp className="w-4 h-4 text-gold/40" />
      </div>
      <div className="font-h text-4xl text-white">{value}</div>
      <div className="text-xs uppercase tracking-widest text-gray-400 mt-1">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const { activeTeam, activeTeamId } = useTeams();
  const [plays, setPlays] = useState([]);
  const [roster, setRoster] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [studyStats, setStudyStats] = useState(null);
  const [inbox, setInbox] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, r, a, n] = await Promise.all([
          api.get('/plays'),
          api.get('/roster'),
          api.get('/assignments').catch(() => ({ data: { assignments: [] } })),
          api.get('/notes/inbox').catch(() => ({ data: { conversations: [] } })),
        ]);
        setPlays(p.data.plays || []);
        setRoster(r.data.players || []);
        setAssignments(a.data.assignments || []);
        setInbox(n.data.conversations || []);
        if (activeTeamId) {
          api.get(`/study/team/${activeTeamId}`)
            .then(rr => setStudyStats(rr.data))
            .catch(() => {});
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [activeTeamId]);

  const top3 = [...plays].sort((a, b) => b.success_rate - a.success_rate).slice(0, 3);
  const byCat = plays.reduce((acc, p) => { acc[p.category] = (acc[p.category] || 0) + 1; return acc; }, {});
  const avgSuccess = plays.length ? Math.round((plays.reduce((s, p) => s + p.success_rate, 0) / plays.length) * 100) : 0;

  return (
    <Layout>
      <div className="mb-8">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">{t('nav_dashboard')}</div>
        <h1 className="font-h uppercase text-4xl sm:text-5xl">{t('hello')}, <span className="text-gold">{user?.name}</span></h1>
        <p className="text-gray-400 mt-2">{t('app_tagline')}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat icon={BookOpen} label={t('total_plays')} value={plays.length} accent />
        <Stat icon={Users} label={t('active_players')} value={roster.length} />
        <Stat icon={Trophy} label={t('success_rate')} value={`${avgSuccess}%`} />
        <Stat icon={Bot} label={t('nav_ai')} value="ON" accent />
      </div>

      {/* This-week ops widget */}
      {(user?.role === 'coach' || user?.role === 'admin') && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10" data-testid="coach-this-week">
          {/* Upcoming tasks */}
          <Link to="/assignments" className="bg-card-steel border border-gold/40 rounded-lg p-5 hover-lift block">
            <div className="flex items-center gap-2 mb-3 text-gold text-xs font-mono uppercase tracking-[0.3em]">
              <ClipboardList className="w-4 h-4" /> {t('dash_this_week') || 'Esta semana'}
            </div>
            <div className="font-h text-3xl text-white">
              {assignments.filter(a => !a.due_date || new Date(a.due_date) >= new Date()).length}
            </div>
            <div className="text-sm text-gray-400 mt-1">{t('dash_upcoming_tasks') || 'tareas activas'}</div>
            {assignments.slice(0, 2).map(a => (
              <div key={a.id} className="mt-2 text-xs text-gray-300 truncate border-t border-gold/15 pt-2 flex items-center gap-2">
                <Calendar className="w-3 h-3 text-gold flex-shrink-0" />
                <span className="truncate">{a.title}</span>
              </div>
            ))}
          </Link>

          {/* Study tracking — least active players */}
          <Link to="/study" className="bg-card-steel border border-gold/40 rounded-lg p-5 hover-lift block">
            <div className="flex items-center gap-2 mb-3 text-gold text-xs font-mono uppercase tracking-[0.3em]">
              <Activity className="w-4 h-4" /> {t('dash_study_focus') || 'Estudio del equipo'}
            </div>
            {studyStats?.players?.length > 0 ? (
              <>
                <div className="font-h text-3xl text-white">
                  {studyStats.players.filter(p => (p.total_time_seconds || 0) === 0).length}
                </div>
                <div className="text-sm text-gray-400 mt-1">{t('dash_inactive_players') || 'jugadores sin actividad'}</div>
                <div className="mt-3 space-y-1">
                  {studyStats.players.slice(0, 2).map(p => (
                    <div key={p.roster_id} className="text-xs text-gray-300 flex items-center gap-2">
                      <span className="truncate flex-1">{p.name}</span>
                      <span className="text-gold font-mono">{p.plays_viewed} jugadas</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-sm text-gray-400">{t('dash_no_study_yet') || 'Aún sin datos'}</div>
            )}
          </Link>

          {/* Unread notes */}
          <Link to="/notes" className="bg-card-steel border border-gold/40 rounded-lg p-5 hover-lift block">
            <div className="flex items-center gap-2 mb-3 text-gold text-xs font-mono uppercase tracking-[0.3em]">
              <MessageSquare className="w-4 h-4" /> {t('dash_inbox') || 'Bandeja de notas'}
            </div>
            <div className="font-h text-3xl text-white">
              {inbox.reduce((s, c) => s + (c.unread || 0), 0)}
            </div>
            <div className="text-sm text-gray-400 mt-1">{t('dash_unread') || 'mensajes sin leer'}</div>
            {inbox.filter(c => c.unread > 0).slice(0, 2).map(c => (
              <div key={c.other_user_id} className="mt-2 text-xs text-gray-300 truncate border-t border-gold/15 pt-2">
                <span className="text-gold">{c.other_user_name}:</span> {c.last_message}
              </div>
            ))}
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-card-steel border border-gold/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-h uppercase text-xl">{t('top_plays')}</h2>
            <Link to="/playbook" className="text-gold text-xs uppercase tracking-widest hover:underline">{t('view_all')} →</Link>
          </div>
          <div className="space-y-3">
            {loading && <div className="text-gray-400 text-sm">{t('loading')}</div>}
            {top3.map(p => (
              <Link
                key={p.id}
                to={`/playbook/${p.id}`}
                className="flex items-center justify-between p-3 rounded-md border border-steel hover:border-gold/60 transition"
              >
                <div>
                  <div className="font-h uppercase text-white">
                    {(() => {
                      const { displayName, purchased } = displayPlayName(p);
                      return <>{displayName}{purchased && <span className="text-[10px] text-gold ms-1">{t('purchased_badge')}</span>}</>;
                    })()}
                  </div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider">{p.category} · {p.subcategory}</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-gold font-mono">{Math.round(p.success_rate * 100)}%</div>
                  <ChevronRight className="w-4 h-4 text-gold" />
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="bg-card-steel border border-gold/30 rounded-lg p-6">
          <h2 className="font-h uppercase text-xl mb-4">{t('distribution')}</h2>
          <div className="space-y-2.5">
            {Object.entries(byCat).map(([k, v]) => (
              <div key={k}>
                <div className="flex items-center justify-between text-xs uppercase tracking-wider mb-1">
                  <span className="text-gray-300">{k}</span>
                  <span className="text-gold font-mono">{v}</span>
                </div>
                <div className="h-1.5 bg-navy-deep rounded">
                  <div className="h-full bg-gold rounded" style={{ width: `${Math.min(100, (v / plays.length) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
          <Link
            to="/ai-coach"
            className="mt-6 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-gold text-navy font-h uppercase tracking-wider text-sm hover:bg-gold-light transition"
          >
            <Bot className="w-4 h-4" /> {t('open_ai_coach')}
          </Link>
        </div>
      </div>
    </Layout>
  );
}
